import type NotificationDetailsModel from "@/core/models/notification-detail";
import { appAxios } from "@/core/utils/appAxios";
import { environment } from "@/environments/environment";
import { cartModel } from "@/pages/shopping-cart/shopping-cart.model";
import type { VehicleDetailsComeFrom } from "@/pages/auth/vehicle-details/vehicle-details";
import {
    defaultPaginationQueryParameters,
    emptyVehicleQueryParameters,
    vehiclesModel,
} from "@/shared/vehicles/vehicles.model";
import { routerModel } from "@/router/router.model";
import {
    type APIResponse,
    VehicleLabel,
    EnumCompanyStatus,
    type GetVehicleGroupsBody,
    type GetVehiclesBody,
    VehicleSortAttribute,
    SortDirection,
    EnumUserRole,
    type PaginationQueryParameters,
    type GetPublicVehiclesQueryParams,
} from "@/shared/api-contract/api-contract";
import { appLanguageSwitcherModel } from "@/shared/components/app-language-switcher/app-language-switcher.model";
import AppLanguageSwitcherComponent from "@/shared/components/app-language-switcher/app-language-switcher.vue";
import PopupCart from "@/shared/components/base/components/popup-cart/popup-cart.vue";
import { headerModel } from "@/shared/components/base/models/header-model";
import { useIsTablet } from "@/shared/composables/use-is-tablet";
import { useWindow } from "@/shared/composables/use-window";
import type Response from "@/shared/models/response";
import { currentUserModel } from "@/shared/state/current-user.model";
import { watch, watchEffect } from "@vue/runtime-core";
import axios from "axios";
import { comparer } from "mobx";
import { shallowRef } from "vue";
import { setup } from "vue-class-component";
import { get } from "lodash";
import { Options, Prop, Ref, Vue } from "vue-property-decorator";
import { useRouter } from "vue-router";
import { appCompanyStatusDialogModel } from "../app-company-status-dialog/app-company-status-dialog.model";
import { AppLanguageCode } from "@/shared/i18n/translations";
import { sessionModel } from "@/shared/state/session.model";
import { currentCompanyModel } from "@/shared/state/current-company.model";
import { useI18n } from "@/shared/composables/use-i18n";
import NavigationButtons from "./NavigationButtons.vue";
import { getEnvValue } from "@/shared/models/environment";
import { BRAND_NAMES } from "@/shared/models/page-title";
export enum HeaderType {
    transparent = "transparent",
    white = "white",
}

type HeaderMode = "default" | "car-details" | "vehicle-details";

const HIDE_NAVIGATION_AND_BUTTONS_BRANDS = ["astara"];

@Options({
    components: {
        "app-language-switcher": AppLanguageSwitcherComponent,
        PopupCart,
        NavigationButtons,
    },
})
export class HeaderComponent extends Vue {
    @Prop({ default: HeaderType.transparent }) type!: HeaderType;
    @Ref("header")
    header?: HTMLElement;
    appLanguageSwitcherModel = appLanguageSwitcherModel;
    routerModel = routerModel;
    router = setup(useRouter);
    pageYOffset = 0;
    isNotificationsOpen = false;
    headerModel = headerModel;
    windowCtx = setup(useWindow);
    isTabletCtx = setup(() => useIsTablet(1200));
    lang = appLanguageSwitcherModel.lang.value;
    get isMobile() {
        return this.isTabletCtx.isTablet.matches;
    }

    condition = [] as readonly number[];
    fuelType = [] as readonly number[];
    brand = [] as readonly number[];
    model = [] as readonly string[];
    i18n = setup(useI18n);

    get isVehicleDetails() {
        return this.$route.path.startsWith("/auth/vehicle-details");
    }
    get isJWTUserDtoPresent() {
        return !!sessionModel.JWTUserDto.value?.id_contact;
    }

    get JWTUserDto() {
        return sessionModel.JWTUserDto.value;
    }

    @Prop()
    vehicleIds?: Array<number>;

    sessionModel = sessionModel;
    cartModel = cartModel;

    disposers: (() => void)[] = [];

    destroyMobileMenu!: () => void;

    openMobileMenu = false;

    showCart = false;

    isManyNews = false;

    comeFrom?: VehicleDetailsComeFrom = setup(() => shallowRef());

    previousCarId?: number = 0;
    nextCarId?: number = 0;

    userAvatarPresent = false;

    ctx = setup(() => {
        const logout = () => {
            sessionModel.logout();
            vehiclesModel.resetDefaultFilters();
            routerModel.router.push("/");
            if (window.location.pathname === "/") {
                window.location.reload();
            }
        };

        const mobileMenu = [
            {
                href: "/auth/my-carconnex/my-orders/to_approve",
                class: "icon__order",
                sprite: "orders",
                i18n: "mobileMenu.orders",
            },
            {
                href: "/auth/my-carconnex/my-car-history",
                class: "icon__files",
                sprite: "docs",
                i18n: "mobileMenu.documents",
            },
            {
                href: "/auth/my-carconnex/my-favorites",
                class: "icon__heart",
                sprite: "favourite",
                i18n: "mobileMenu.favourites",
            },
            {
                href: "/auth/my-carconnex/my-settings",
                class: "icon__gear",
                sprite: "settings2",
                i18n: "mobileMenu.settings",
            },
            {
                href: "/auth/my-carconnex/my-account/contact-info",
                class: "icon__user",
                sprite: "profile-user-stroke",
                i18n: "mobileMenu.account",
            },
        ];

        const headerDropdownMenu = [
            {
                href: "/auth/my-carconnex",
                class: "icon__order",
                sprite: "filter-kilometers",
                i18n: "dashboardSidebar.dashboard",
                onlyApproveCompany: true,
            },
            {
                href: "/auth/my-carconnex/my-orders/to_approve",
                class: "icon__order",
                sprite: "orders",
                i18n: "mobileMenu.orders",
                onlyApproveCompany: true,
            },
            {
                href: "/auth/my-carconnex/my-favorites",
                class: "icon__heart",
                sprite: "favourite",
                i18n: "mobileMenu.favourites",
                onlyApproveCompany: false,
            },
            // {
            //     href: "/auth/my-carconnex/my-settings",
            //     class: "icon__gear",
            //     sprite: "settings2",
            //     i18n: "mobileMenu.settings",
            //     onlyApproveCompany: false,
            // },
            {
                href: "/auth/my-carconnex/my-account/contact-info",
                class: "icon__user",
                sprite: "profile-user-stroke",
                i18n: "mobileMenu.account",
                onlyApproveCompany: false,
            },
        ];

        return {
            logout,
            mobileMenu,
            headerDropdownMenu,
        };
    });

    toggleMobileMenu = () => {
        this.openMobileMenu = !this.openMobileMenu;
    };

    get fetchVehiclesFilterStatisticsQuery(): GetPublicVehiclesQueryParams {
        return {
            fueltype: this.fuelType,
            brand: this.brand,
            model: this.model,
            condition: this.condition,
        };
    }

    get fetchVehiclesQueryForLink(): PaginationQueryParameters &
        GetPublicVehiclesQueryParams {
        return {
            ...defaultPaginationQueryParameters,
            ...this.fetchVehiclesFilterStatisticsQuery,
        };
    }

    get getFirstname() {
        return sessionModel.JWTUserDto.value?.family_name;
    }

    navigateAndCloseSidebar(navigate: () => void) {
        this.headerModel.toggleSidebar(false);
        navigate();
    }

    isLastVehicleInList() {
        const currentVehicleId = Number.parseInt(
            routerModel.router.currentRoute.value.params.id as string,
            10,
        );

        const index = this.vehicleIds?.findIndex(
            (vehicleDto) => vehicleDto === currentVehicleId,
        );
        if (this.vehicleIds) {
            return index === this.vehicleIds?.length - 1;
        }
    }

    isFirstVehicleInList() {
        const currentVehicleId = Number.parseInt(
            routerModel.router.currentRoute.value.params.id as string,
            10,
        );

        const index = this.vehicleIds?.findIndex(
            (vehicleDto) => vehicleDto === currentVehicleId,
        );
        if (this.vehicleIds) {
            return index === 0;
        }
    }

    navigateToVehicle(direction: "next" | "previous") {
        const currentVehicleId = Number.parseInt(
            routerModel.router.currentRoute.value.params.id as string,
            10,
        );
        let targetVehicleId: number | undefined;
        const vehicleIds = this.vehicleIds;

        if (direction === "next" && vehicleIds) {
            const nextVehicleIndex = vehicleIds?.findIndex(
                (vehicleDto) => vehicleDto === currentVehicleId,
            );
            if (nextVehicleIndex !== -1) {
                if (nextVehicleIndex === vehicleIds?.length - 1) {
                    targetVehicleId = vehicleIds[0];
                } else {
                    targetVehicleId = vehicleIds[nextVehicleIndex + 1];
                }
            }
        } else if (direction === "previous" && vehicleIds) {
            const previousVehicleIndex = vehicleIds?.findIndex(
                (vehicleDto) => vehicleDto === currentVehicleId,
            );
            if (previousVehicleIndex !== -1) {
                if (previousVehicleIndex === 0) {
                    targetVehicleId = vehicleIds[vehicleIds.length - 1];
                } else {
                    targetVehicleId = vehicleIds[previousVehicleIndex - 1];
                }
            }
        }
        return targetVehicleId;
    }
    get isAuthenticated() {
        return !!sessionModel.sessionDto.value;
    }

    routeToWorkInProgress() {
        this.headerModel.toggleSidebar(false);
        routerModel.router.push({
            path: "/work-in-progress",
        });
    }

    routeToDashboard() {
        this.headerModel.toggleSidebar(false);
        routerModel.router.push({
            path: "/auth/my-carconnex",
        });
    }

    get canUseCart() {
        return (
            sessionModel.JWTUserDto.value &&
            sessionModel.JWTUserDto.value?.id_role >= EnumUserRole.PURCHASOR
        );
    }

    get languageIsNotEnglish() {
        const lang = get(this.appLanguageSwitcherModel, "lang", AppLanguageCode.en);

        return lang !== AppLanguageCode.en;
    }

    get previousCarIdLoading(): boolean {
        return (
            this.previousCarId ===
            Number.parseInt(routerModel.router.currentRoute.value.params.id as string, 10)
        );
    }

    get nextCarIdLoading(): boolean {
        return (
            this.nextCarId ===
            Number.parseInt(routerModel.router.currentRoute.value.params.id as string, 10)
        );
    }

    get isApprovedCompany() {
        return (
            currentCompanyModel.companyDto.value !== undefined &&
            currentCompanyModel.companyDto.value?.status.id === EnumCompanyStatus.APPROVED
        );
    }

    get comeFromURL() {
        const suffix =
            sessionModel.sessionDto.value && currentUserModel.userDto.value?.is_enabled
                ? "vehicles"
                : "public/vehicles-grouped";

        return this.comeFrom?.source === "car-group"
            ? `/client/web/${suffix}/car-groups/${this.comeFrom.id}`
            : `/client/web/${suffix}`;
    }

    get comeFromParams() {
        switch (this.comeFrom?.source) {
            case "customized-deals": {
                return {
                    ...emptyVehicleQueryParameters,
                    quick_selection: [VehicleLabel.specialDeal],
                    sort: SortDirection.asc,
                    sort_by: VehicleSortAttribute.price,
                };
            }
            default:
                return {
                    ...this.comeFrom?.query,
                    /* quick_selection: TODO!
                        !this.comeFrom?.data?.quick_selection?.includes(
                            VehicleLabel.specialDeal,
                        ) &&
                        (!authModel.accessToken.value ||
                            !authModel.accessToken.value.user.approved)
                            ? [
                                  ...(this.comeFrom?.data?.quick_selection || []),
                                  VehicleLabel.specialDeal,
                              ]
                            : this.comeFrom?.data.quick_selection || [], */
                };
        }
    }

    routeToCarDetails(navigate: () => void, isSpecial = false) {
        if (isSpecial) {
            navigate();
        }
    }
    async mounted() {
        await this.cartModel.fetchShoppingCart();
        if (
            routerModel.router.currentRoute.value.path.startsWith(
                "/auth/vehicle-details/",
            )
        ) {
            this.nextCarId = this.navigateToVehicle("next");
            this.previousCarId = this.navigateToVehicle("previous");
        }

        watch(
            () => ({
                w: this.windowCtx.w,
            }),
            () => {
                this.onHeaderHeightChange();
            },
            { immediate: true },
        );

        watchEffect(() => {
            if (this.windowCtx.w.pageYOffset > 10) {
                document.body.classList.add("is-scrolled");
            } else {
                document.body.classList.remove("is-scrolled");
            }
        });

        watchEffect(() => {
            if (routerModel.router.currentRoute.value.query.come_from) {
                this.comeFrom = JSON.parse(
                    routerModel.router.currentRoute.value.query.come_from as string,
                );
            } else {
                this.comeFrom = {
                    source: "vehicles",
                    query: emptyVehicleQueryParameters,
                };
            }
        });

        watch(
            () => ({
                lang: appLanguageSwitcherModel.lang.value,
                accessToken: sessionModel.sessionDto.value,
                comeFrom: this.comeFrom,
                comeFromParams: this.comeFromParams,
                comeFromURL: this.comeFromURL,
                headerMode: this.headerMode,
                currentCarId: Number.parseInt(
                    routerModel.router.currentRoute.value.params.id as string,
                    10,
                ),
            }),
            async (value, oldValue) => {
                if (
                    !value.comeFrom ||
                    value.headerMode !== "car-details" ||
                    comparer.structural(value, oldValue)
                ) {
                    return;
                }

                const response = await appAxios.value.get<
                    APIResponse<GetVehiclesBody | GetVehicleGroupsBody>
                >(value.comeFromURL, {
                    params: {
                        ...value.comeFromParams,
                        include_filters: false,
                        /* per_page: Math.max(
                            value?.comeFromParams?.per_page || 0,
                            AppPaginationPerPage._12,
                        ), */
                    },
                });

                /** const { nextCarId, previousCarId } = eff.fn.pipe(
                    response.data.data.data,
                    eff.array.map((vehicle: PublicVehicleDto | VehicleGroupDto) =>
                        "cars" in vehicle ? vehicle.cars : [vehicle],
                    ),
                    eff.array.flatten,
                    (cars) =>
                        eff.fn.pipe(
                            cars,
                            eff.array.findIndex((car) => car.id === value.currentCarId),
                            eff.option.fold(
                                () => ({ nextCarId: 0, previousCarId: 0 }),
                                (currentCarIndex) => ({
                                    nextCarId: eff.fn.pipe(
                                        currentCarIndex + 1 in cars
                                            ? eff.option.some(cars[currentCarIndex + 1])
                                            : eff.option.none,
                                        eff.option.fold(
                                            () => 0,
                                            ({ id }) => id,
                                        ),
                                    ),
                                    previousCarId: eff.fn.pipe(
                                        currentCarIndex - 1 in cars
                                            ? eff.option.some(cars[currentCarIndex - 1])
                                            : eff.option.none,
                                        eff.option.fold(
                                            () => 0,
                                            ({ id }) => id,
                                        ),
                                    ),
                                }),
                            ),
                        ),
                ); **/

                this.nextCarId = 0; // nextCarId; TODO
                this.previousCarId = 0; // previousCarId; TODO
            },
            { immediate: true },
        );

        // this.destroyMobileMenu = initMobileMenu();

        this.checkNotifications().then();
    }

    get headerMode(): HeaderMode {
        if (this.router.currentRoute.path.startsWith("/vehicle-details/")) {
            return "car-details";
        }
        return "default";
    }

    get query() {
        return routerModel.router.currentRoute.value.query;
    }

    back() {
        routerModel.back("/vehicles-grouped");
    }

    unmounted() {
        headerModel.changeHeight(0);
        this.disposers.forEach((disposer) => disposer());
        if (this.destroyMobileMenu) {
            this.destroyMobileMenu();
        }
    }

    get headerClasses() {
        return {
            header_white:
                this.type === HeaderType.white || this.windowCtx.w.pageYOffset > 10,
            header_transparent:
                this.type === HeaderType.transparent &&
                this.windowCtx.w.pageYOffset <= 10,
        };
    }

    get isHidden() {
        const id = routerModel.router.currentRoute.value.params.id;

        return (
            !headerModel.isSidebarOpen.value &&
            ["/vehicles-grouped", `/car-group/${id}`].includes(
                routerModel.router.currentRoute.value.path,
            ) &&
            this.windowCtx.w.pageYOffset > 10 &&
            !this.isTabletCtx.isTablet.matches
        );
    }

    toggleNotifications(value = !this.isNotificationsOpen) {
        this.isNotificationsOpen = value;
    }

    cartLinkClick(event: MouseEvent) {
        const isCartPage = this.$router.currentRoute.value.path === "/cart";
        if (isCartPage) {
            return;
        }
        const popupCart = document.querySelector(".popup-cart");
        if (!popupCart) {
            return;
        }
        // this check is necessary because popup div is placed inside <a>
        // so we need to skip all clicks inside popup div
        if (event.target instanceof Element && !popupCart.contains(event.target)) {
            this.showCart = !this.showCart;
        }
    }

    onHeaderHeightChange() {
        headerModel.changeHeight(this.header?.offsetHeight || 0);
    }

    showCompanyStatusDialog(comingSoon?: boolean): boolean {
        return appCompanyStatusDialogModel.show(comingSoon ? "comingSoon" : "default");
    }

    /* API Connectivity --------------------------------------------------------------------------------------------- */

    private async checkNotifications() {
        await axios
            .get(`${environment.apiUrl}news/${appLanguageSwitcherModel.lang.value}`, {
                params: { page: 0, perPage: 1000 },
            })
            .then((res: Response<NotificationDetailsModel>) => {
                this.isManyNews = res.data.total > 0;
            })
            .catch(console.log);
    }

    get brandLabel() {
        return getEnvValue("BRAND_LABEL");
    }

    get shouldHideNavigationAndButtons() {
        return HIDE_NAVIGATION_AND_BUTTONS_BRANDS.includes(this.brandLabel);
    }

    get brandName() {
        return BRAND_NAMES[this.brandLabel];
    }
}
